import queryString from 'query-string';
import { TRC_TYPE, TRC_RESULT_PAGES, CS_MEASURES } from '../../constants/assessments';
import { PERIOD } from '../../constants/enrollment';
import {
    R3D,
    R3DSPANISH,
    DIBELS8,
    BURST,
    ESPANOL,
    IDEL,
    IDEL2,
    MATH_APP,
} from '../../constants/applications';

const NEW_TAB_OPTION = '_blank';

const DASHBOARD_PAGE_NAME = 'Dashboard';

export const PAGE_TYPE = {
    REMINDERS: 'reminders',
    REVIEW: 'review',
    BENCHMARK: 'benchmark',
    PROGMON: 'progmon',
};

export const CUSTOMER_REQUIREMENTS_LINK = 'https://www.amplify.com/customer-requirements/';
export const CUSTOMER_SERVICE_LINK = 'https://www.amplify.com/support/';
export const HOW_TO_ASSESS_MAZE_LINK =
    'https://mclass.amplify.com/support_center/HowTo_D8_Maze.pdf';
export const HOW_TO_ASSESS_CP_LINK = 'https://mclass.amplify.com/support_center/Guia_rapida_CP.pdf';
export const HOW_TO_ASSESS_SUMMER_LINK = 'https://learning.amplify.com/pdlibrary';

// cache-friendly URL
const OWA_INDEX = '/assessment/';

export const constructAppClassListLink = (appName, selectedClassId) =>
    `${OWA_INDEX}#${appName}/classe/classeId=${selectedClassId}`;

export const constructAppLink = (appName, params = {}) =>
    `${OWA_INDEX}#${appName}/assessment/selection?${queryString.stringify(params)}`;

export const constructAppSummaryLink = (appName, summaryName, params) =>
    `${OWA_INDEX}#${appName}/summary/${summaryName}/${queryString.stringify(params)}`;

const getDashboardBackNavigationParams = (studentId, classId, period, groupName) => ({
    studentId,
    classeId: classId,
    period,
    groupName,
    from: DASHBOARD_PAGE_NAME,
});

export const constructIdel1BMSummaryLink = (studentId, classId, period) =>
    constructAppSummaryLink(
        IDEL,
        PAGE_TYPE.BENCHMARK,
        getDashboardBackNavigationParams(studentId, classId, period, ESPANOL),
    );

export const constructIdel1PMSummaryLink = (studentId, classId, period) =>
    constructAppSummaryLink(
        IDEL,
        PAGE_TYPE.PROGMON,
        getDashboardBackNavigationParams(studentId, classId, period, ESPANOL),
    );

export const constructDibels8Link = (studentId, measure, pageType, params) =>
    `/dibels8-assessment/${studentId}/${measure.toLowerCase()}/${pageType}?${queryString.stringify(
        params,
    )}`;

export const constructIdel2Link = (studentId, measure, pageType, params) =>
    `/espanol-assessment/${studentId}/${measure.toLowerCase()}/${pageType}?${queryString.stringify(
        params,
    )}`;

export const navigateToPath = (path, checkAndGetSessionStatus) => {
    // checkAndGetSessionStatus is passed from OWA.
    // It runs a session check and returns true if session is expired
    // If true, user will see login popup and dashboard navigation won't happen
    if (typeof checkAndGetSessionStatus !== 'function' || !checkAndGetSessionStatus()) {
        // If false, dashboard navigation happens
        window.location.assign(path);
    }
};

/**
 * OnClick handler for assess and measure score icons.
 * Handler navigates user to site and prevents new tab creation in iPad app mode
 * @param {string} path to assessment
 * @returns {Function} on click handler
 */
export const linkOnClickHandler = (path, checkAndGetSessionStatus) => event => {
    if (event && event.preventDefault && typeof event.preventDefault === 'function')
        event.preventDefault();
    navigateToPath(path, checkAndGetSessionStatus);
};

export const navigateToDibels8 = (studentId, measure, pageType, params) => _event => {
    navigateToPath(constructDibels8Link(studentId, measure, pageType, params));
};

export const navigateToAppClassList = (appName, selectedClassId) => {
    window.location.assign(constructAppClassListLink(appName, selectedClassId));
};

export const navigateToMathClassList = selectedClassId => {
    window.location.assign(constructAppClassListLink(MATH_APP, selectedClassId));
};

export const navigateToTRCDetails = (navigationParams, accessLink) => _event => {
    if (accessLink) {
        window.location.assign(accessLink);
    } else {
        const { application = R3D, ...queryParams } = navigationParams;
        window.location.assign(
            `${OWA_INDEX}#${application}/assessment/trc-result/${queryString.stringify(
                queryParams,
            )}&measure=TRC`,
        );
    }
};
export const navigateToD8EditionHelper = () =>
    window.open(`/wgenhelp/md8e/index.htm#t=DIBELS_8th_Edition.htm`, NEW_TAB_OPTION);

export const navigateToD8TexasEditionHelper = () =>
    window.open(`/wgenhelp/mtxd8e/index.htm#t=DIBELS_8th_Edition.htm`, NEW_TAB_OPTION);

export const navigateToD8EditionAndTRCHelper = () =>
    window.open(`/wgenhelp/md8ent/index.htm#t=mCLASS_with_TRC.htm`, NEW_TAB_OPTION);

export const navigateToD8TexasEditionAndTRCHelper = () =>
    window.open(`/wgenhelp/mtxd8ent/index.htm#t=mCLASS_with_TRC.htm`, NEW_TAB_OPTION);

export const navigateToOLSHelper = () =>
    window.open(`/wgenhelp/mol/index.htm#t=mCLASS_Oral_Language.htm`, NEW_TAB_OPTION);

export const navigateToIDELHelper = () =>
    window.open(
        `/wgenhelp/idel2/index.htm#t=IDEL%2FGetting_Started%2FThe_mCLASS_IDEL_System2.htm`,
        NEW_TAB_OPTION,
    );

export const navigateToLecturaHelper = () =>
    window.open(`/wgenhelp/mce_lec/index.htm?rscsh=1&rhmapid=Lectura`, NEW_TAB_OPTION);

export const navigateToIDELTexasHelper = () =>
    window.open(
        `/wgenhelp/mtxidel/index.htm#t=IDEL%2FGetting_Started%2FThe_mCLASS_IDEL_System2.htm`,
        NEW_TAB_OPTION,
    );

export const navigateToTRCSpanishHelper = () =>
    window.open(`/wgenhelp/mcetrc/index.htm#t=TRC_Espanol.htm`, NEW_TAB_OPTION);

export const navigateToOLSSpanishHelper = () =>
    window.open(`/wgenhelp/mceols/index.htm#t=mCLASS_Oral_Language_Espanol.htm`, NEW_TAB_OPTION);

export const navigateToMathHelper = () =>
    window.open(
        `/wgenhelp/math2/index.htm#t=Getting_Started%2FThe_mCLASS_Math_System.htm`,
        NEW_TAB_OPTION,
    );

export const navigateToReadingClassroomReports = () =>
    window.open('/reading-classroom-reports/', NEW_TAB_OPTION);

export const navigateToSpanishClassroomReports = () =>
    window.open('/espanol-classroom-reports/', NEW_TAB_OPTION);

export const changeClasses = () =>
    window.location.assign(`${OWA_INDEX}#classeSelection/changingClasses=true&from=Dashboard`);

export const changeSchool = () =>
    window.location.assign(`${OWA_INDEX}#classeSelection/changingInstitution=true`);

export const constructTRCAssessmentLink = ({ application = R3D, ...queryParams }) =>
    `${OWA_INDEX}#${application}/assessment/trc-select/${queryString.stringify(
        queryParams,
    )}&measure=TRC`;

export const constructTRCPMAssessmentLink = ({ application = R3D, ...params }) =>
    `${OWA_INDEX}#${application}/assessment/progmon/select/${queryString.stringify(
        params,
    )}&measure=TRC&period=PM`;

export const constructBurstAssessmentLink = params =>
    `${OWA_INDEX}#burst/assessment/practice/${queryString.stringify(params)}`;

export const constructBurstAssessmentCSLink = params =>
    `${OWA_INDEX}#burst/assessment/instructions/${queryString.stringify(params)}`;

export const constructStartPMAssessmentLink = (application, classId, studentId, params) => {
    switch (application) {
        case R3D:
        case R3DSPANISH: {
            const trcParams = {
                classeId: classId,
                application,
                studentId,
            };
            return constructTRCPMAssessmentLink(trcParams);
        }
        case DIBELS8: {
            const { measure, formUid } = params;
            return constructDibels8Link(studentId, measure, PAGE_TYPE.REMINDERS, {
                period: PERIOD.PM,
                classId,
                formUid,
            });
        }
        case IDEL2: {
            const { measure, formUid } = params;
            return constructIdel2Link(studentId, measure, PAGE_TYPE.REMINDERS, {
                period: PERIOD.PM,
                classId,
                formUid,
            });
        }
        case BURST: {
            const { measure, probeConfig } = params;
            if (probeConfig) {
                const assessmentParams = {
                    studentId,
                    classeId: classId,
                    period: PERIOD.PM,
                    measure,
                    probeId: probeConfig.probeId,
                };
                return CS_MEASURES.includes(measure)
                    ? constructBurstAssessmentCSLink({
                          ...assessmentParams,
                          genre: probeConfig.genre,
                          seq: probeConfig.sequence,
                      })
                    : constructBurstAssessmentLink(assessmentParams);
            }
            return '';
        }
        default:
            return '';
    }
};

export const constructTRCResumeLink = params => {
    if (params) {
        const { application = R3D, ...queryParams } = params;
        const pausedResultPage = [TRC_TYPE.PC, TRC_TYPE.RB].includes(queryParams.page)
            ? TRC_RESULT_PAGES.PC_RB_RESULT
            : TRC_RESULT_PAGES.RR_RESULT;
        return `${OWA_INDEX}#${application}/assessment/${pausedResultPage}/${queryString.stringify(
            queryParams,
        )}&measure=TRC`;
    }
    return undefined;
};

export const constructBurstScoreLink = params =>
    `${OWA_INDEX}#burst/assessment/score/${queryString.stringify(params)}`;

export const constructOLResumeLink = params =>
    `${OWA_INDEX}#r3d/assessment/ol-item/${queryString.stringify(params)}&measure=OL`;

export const constructOLAssessmentLink = params =>
    `${OWA_INDEX}#r3d/assessment/instructions/${queryString.stringify(params)}&measure=OL`;

export const constructOLReviewLink = params =>
    `${OWA_INDEX}#r3d/assessment/ol-result/${queryString.stringify(params)}&measure=OL`;

export const navigateToOLDetails = navigationDetails => _event =>
    window.location.assign(constructOLReviewLink(navigationDetails));

export const constructOLSAssessmentLink = (studentId, params) =>
    `/oral-language-assessment/${studentId}/reminders?${queryString.stringify(params)}`;

export const constructOLSReviewLink = (studentId, params) =>
    `/oral-language-assessment/${studentId}/review?${queryString.stringify(params)}`;

export const navigateToTRCBMAssessment = navigationDetails => _event => {
    navigateToPath(constructTRCAssessmentLink(navigationDetails));
};

export const DASHBOARD_URL_HASH = '#dashboard/';

export const setURLQueryParams = params => {
    window.history.pushState(null, null, `${DASHBOARD_URL_HASH}${queryString.stringify(params)}`);
};

export const getURLQueryParams = () =>
    queryString.parse(window.location.hash.replace(DASHBOARD_URL_HASH, ''));

export const updateURLQueryParams = params => {
    setURLQueryParams({ ...getURLQueryParams(), ...params });
};

export const DEFAULT_FLO_PM_POPUP_LINK = ' https://pd.ci.amplify.com';
